import { MESSAGES } from "@constants/texts/messages";
import { extractApiErrorMessage } from "@utils/common";
import consoleLogFormatted from "consoleLogFormatted";
import pRetry, { AbortError } from "p-retry";
const defaultRetryOptions = {
    retries: 3,
    minTimeout: 1000,
    maxTimeout: 8000,
    factor: 2,
    isCritical: false,
    additionalRetryStatusCodes: [],
};
const criticalRetryDefaults = {
    retries: 5,
    minTimeout: 2000,
    maxTimeout: 30000,
    factor: 2,
    additionalRetryStatusCodes: [],
};
const shouldRetry = (error, additionalStatusCodes = []) => {
    var _a;
    if (error.code === "ETIMEDOUT" ||
        error.code === "ECONNRESET" ||
        error.code === "ECONNABORTED" ||
        error.code === "ENOTFOUND" ||
        error.code === "EAI_AGAIN") {
        return true;
    }
    if (error.message && error.message.toLowerCase().includes("timeout")) {
        return true;
    }
    if ((_a = error.response) === null || _a === void 0 ? void 0 : _a.status) {
        const status = error.response.status;
        return (status >= 500 ||
            status === 404 ||
            status === 408 ||
            status === 429 ||
            additionalStatusCodes.includes(status));
    }
    return true;
};
export const withRetry = async (apiCall, options = {}) => {
    const opts = { ...defaultRetryOptions, ...options };
    return pRetry(async () => {
        var _a, _b;
        try {
            return await apiCall();
        }
        catch (error) {
            const axiosError = error;
            if (!shouldRetry(axiosError, opts.additionalRetryStatusCodes)) {
                const abortError = new AbortError((_a = extractApiErrorMessage(axiosError)) !== null && _a !== void 0 ? _a : `Non-retriable error: ${((_b = axiosError.response) === null || _b === void 0 ? void 0 : _b.status) || axiosError.code}`);
                abortError.originalError =
                    axiosError;
                throw abortError;
            }
            throw error;
        }
    }, {
        retries: opts.retries,
        minTimeout: opts.minTimeout,
        maxTimeout: opts.maxTimeout,
        factor: opts.factor,
        onFailedAttempt: error => {
            if (opts.isCritical) {
                consoleLogFormatted.error(MESSAGES.apiWrapper.requestFailed(error.attemptNumber, error.retriesLeft, error.message));
            }
            else {
                consoleLogFormatted.warning(MESSAGES.apiWrapper.requestFailed(error.attemptNumber, error.retriesLeft, error.message));
            }
        },
    });
};
export const withCriticalRetry = (apiCall, options = {}) => withRetry(apiCall, {
    ...criticalRetryDefaults,
    ...options,
    isCritical: true,
});
export const safeApiCall = async (apiCall, fallbackValue, options = {}) => {
    var _a;
    try {
        return await withRetry(apiCall, options);
    }
    catch (error) {
        const axiosError = error;
        consoleLogFormatted.error(MESSAGES.apiWrapper.allRetriesFailed((_a = axiosError === null || axiosError === void 0 ? void 0 : axiosError.message) !== null && _a !== void 0 ? _a : String(error)));
        if (fallbackValue !== undefined) {
            return {
                data: fallbackValue,
                status: 200,
                statusText: "OK",
                headers: {},
                config: {},
            };
        }
        return null;
    }
};
//# sourceMappingURL=apiWrapper.js.map